/**
 * PortTimeline - Timeline component for port calls at a specific port
 *
 * Uses BroadcastChannel to request data from main window instead of
 * making direct API calls (stateless popup pattern).
 */
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  TimelineEvent,
  TimelineEventAction,
  ScrollArea,
} from '@ais-ui/ui-core';
import { TimelineContainer } from '../../components/TimelineContainer';
import { DateRange } from 'react-day-picker';
import { formatVesselName, getCountryFromMmsi } from '@ais-ui/utils';
import {
  PopupActions,
  useMainWindowMessages,
  MessageType,
  PopupMessage,
} from './PopupEvents';
import { usePopupDataState } from './usePopupDataState';
import { PopupRefreshIndicator } from './PopupRefreshIndicator';
import { PiMapPinLine, PiAnchor } from 'react-icons/pi';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import {
  PortCallData,
  usePortCallAggregations,
  PortCallStatsCard,
  PortCallDistributionChart,
  PortCallTimelineChart,
} from '../../components/PortCallAnalytics';

// MID mapping is imported from @ais-ui/utils (getCountryFromMmsi)
/**
 * Get country name from MMSI number
 * @param mmsi - Maritime Mobile Service Identity number
 * @returns Country name or undefined if not found
 */
const getCountryFromMmsiNumber = (
  mmsi: number | undefined,
): string | undefined => {
  if (!mmsi) return undefined;
  return getCountryFromMmsi(mmsi.toString());
};

interface PortTimelineProps {
  locode: string;
  portName?: string;
}

interface PortCall {
  ata?: Date;
  atd?: Date;
  ship?: {
    name?: string;
    mmsi?: number;
    imo?: number;
    shiptype?: string;
    country?: string;
  };
}

interface PortCallsData {
  records: PortCall[];
}

export const PortTimeline = ({ locode, portName }: PortTimelineProps) => {
  // Use the popup data state hook for "stale while revalidate" pattern
  const {
    data: portCallsData,
    isInitialLoading,
    isRefreshing,
    error,
    setData: setPortCallsData,
    setRefreshing,
    setError,
  } = usePopupDataState<PortCallsData>();

  const [requestId] = useState(() => uuidv4());
  const dataReceivedRef = useRef(false);
  const isFirstLoad = useRef(true);
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  // Set default date range: 7 days back from today
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);
    today.setHours(23, 59, 59, 999);

    return {
      from: sevenDaysAgo,
      to: today,
    };
  });

  // Request data from main window
  useEffect(() => {
    // Show refreshing indicator for subsequent loads
    if (!isFirstLoad.current) {
      setRefreshing(true);
    }
    isFirstLoad.current = false;
    dataReceivedRef.current = false;

    PopupActions.requestInit({
      popupId: requestId,
      popupType: 'port-timeline',
      params: {
        locode,
        dateFrom: dateRange?.from?.toISOString(),
        dateTo: dateRange?.to?.toISOString(),
      },
    });

    // Timeout after 15 seconds
    const timeout = setTimeout(() => {
      if (!dataReceivedRef.current) {
        setError('Timeout waiting for data');
      }
    }, 15000);

    return () => clearTimeout(timeout);
  }, [locode, dateRange, requestId, setRefreshing, setError]);

  // Listen for response from main window
  useMainWindowMessages(
    (msg: PopupMessage) => {
      if (
        msg.type === MessageType.POPUP_INIT_RESPONSE &&
        msg.popupId === requestId
      ) {
        dataReceivedRef.current = true;
        const payload = msg.payload as { records?: any[]; error?: string };
        if (payload.error) {
          setError(payload.error);
        } else {
          // Map API response format to internal format
          // API returns: ata, atd, ship (with mmsi but often no country)
          // We derive country from MMSI if not provided
          const records = (payload.records || []).map((record: any) => {
            const ship = record.source?.ship || record.ship;
            return {
              ata: record.ata_ais
                ? new Date(record.ata_ais)
                : record.ata
                  ? new Date(record.ata)
                  : undefined,
              atd: record.atd_ais
                ? new Date(record.atd_ais)
                : record.atd
                  ? new Date(record.atd)
                  : undefined,
              ship: ship
                ? {
                    ...ship,
                    // Use country from API if available, otherwise derive from MMSI
                    country:
                      ship.country || getCountryFromMmsiNumber(ship.mmsi),
                  }
                : undefined,
            };
          });
          setPortCallsData({ records });
        }
      }
    },
    requestId,
    [requestId, setPortCallsData, setError],
  );

  const EVENT_TYPES = useMemo(
    () => [
      {
        id: 'port-call',
        name: 'Port Call',
        color: '#3B82F6',
        disabled: false,
      },
    ],
    [],
  );

  const portCallType = EVENT_TYPES.find((type) => type.id === 'port-call')!;

  // Transform port calls to PortCallData for analytics
  const analyticsData: PortCallData[] = useMemo(() => {
    if (!portCallsData?.records) return [];

    return portCallsData.records.map((portCall, index) => ({
      id: `port-call-${index}`,
      ata: portCall.ata as Date,
      atd: portCall.atd,
      vesselName: formatVesselName(portCall.ship?.name || 'Unknown'),
      vesselMmsi: portCall.ship?.mmsi?.toString(),
      vesselImo: portCall.ship?.imo?.toString(),
      vesselCountry: portCall.ship?.country,
      portName: portName,
      portLocode: locode,
    }));
  }, [portCallsData, portName, locode]);

  // Filter analytics data by date range
  const filteredAnalyticsData = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return analyticsData;

    return analyticsData.filter((call) => {
      return call.ata >= dateRange.from! && call.ata <= dateRange.to!;
    });
  }, [analyticsData, dateRange]);

  // Compute aggregations
  const aggregations = usePortCallAggregations({
    portCalls: filteredAnalyticsData,
    mode: 'port',
  });

  // Convert port calls to timeline events
  const portCallEvents: TimelineEvent[] = useMemo(() => {
    if (!portCallsData?.records) {
      return [];
    }

    return portCallsData.records.map((portCall, index) => {
      const ship = portCall.ship;
      const vesselName = formatVesselName(ship?.name || 'Unknown vessel');

      // Format the duration into a human-readable string
      const formatDuration = (totalSeconds: number) => {
        if (!totalSeconds) return '—';

        const days = Math.floor(totalSeconds / (24 * 3600));
        const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);

        const parts = [];
        if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
        if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
        if (minutes > 0)
          parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

        return parts.length > 0 ? parts.join(', ') : 'less than a minute';
      };

      let stringDuration = '—';
      if (portCall.ata && portCall.atd) {
        const durationInSeconds: number =
          (portCall.atd.getTime() - portCall.ata.getTime()) / 1000;
        stringDuration = formatDuration(durationInSeconds);
      }

      const arrivalString = formatDateTimeWithTimeZone(portCall.ata);
      const departureString = portCall.atd
        ? formatDateTimeWithTimeZone(portCall.atd)
        : '—';

      return {
        id: `port-call-${index}`,
        date: portCall.ata as Date,
        title: vesselName,
        description: `${vesselName} arrived at ${arrivalString} and departed at ${departureString} for a total of ${stringDuration}.`,
        type: portCallType,
        ongoing: !portCall.atd,
        metadata: {
          Vessel: vesselName,
          MMSI: ship?.mmsi?.toString() || 'N/A',
          IMO: ship?.imo?.toString() || 'N/A',
          'Ship Type': ship?.shiptype || 'N/A',
          Country: ship?.country || 'N/A',
          Arrival: arrivalString,
          Departure: departureString,
          Duration: stringDuration,
        },
      };
    });
  }, [portCallsData, portCallType, formatDateTimeWithTimeZone]);

  // Filter events by date range
  const filteredEvents = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) {
      return portCallEvents;
    }

    return portCallEvents.filter((event) => {
      const eventDate = new Date(event.date);
      if (!dateRange.from || !dateRange.to) {
        return true;
      }
      return eventDate >= dateRange.from && eventDate <= dateRange.to;
    });
  }, [portCallEvents, dateRange]);

  // Event actions for the timeline
  const eventActions: TimelineEventAction[] = useMemo(
    () => [
      {
        label: 'Zoom to vessel',
        icon: <PiMapPinLine className="h-5 w-5" />,
        onClick: (event: TimelineEvent) => {
          const mmsi = event.metadata?.MMSI;
          const name = event.title;

          if (mmsi && mmsi !== 'N/A') {
            PopupActions.viewTarget({
              mmsi,
              name: name || undefined,
            });
          }
        },
      },
      {
        label: 'Zoom to port',
        icon: <PiAnchor className="h-5 w-5" />,
        onClick: () => {
          PopupActions.zoomToPort({
            locode,
          });
        },
      },
    ],
    [locode],
  );

  // Handle port call selection from stats card - zoom to vessel
  const handleSelectPortCall = useCallback(
    (portCallId: string) => {
      const portCall = analyticsData.find((pc) => pc.id === portCallId);
      if (portCall?.vesselMmsi) {
        PopupActions.viewTarget({
          mmsi: portCall.vesselMmsi,
          name: portCall.vesselName,
        });
      }
    },
    [analyticsData],
  );

  if (error) {
    return (
      <TimelineContainer
        isLoading={isInitialLoading}
        events={[]}
        eventTypes={EVENT_TYPES}
        className="mb-12 pl-6 pr-10"
        title={`Port calls at ${portName || locode}`}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        emptyMessage="Error loading port call history"
      />
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="space-y-4 pb-4 relative">
        {/* Refresh indicator */}
        <PopupRefreshIndicator
          isRefreshing={isRefreshing}
          message="Updating port calls..."
        />

        {/* Timeline */}
        <TimelineContainer
          isLoading={isInitialLoading}
          events={filteredEvents}
          eventTypes={EVENT_TYPES}
          className="mb-12 pl-6 pr-10"
          title={`Port calls at ${portName || locode}`}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          eventActions={eventActions}
          listMode="port"
          viewModeStorageKey="port-timeline-view-mode"
        />

        {/* Analytics Section */}
        {!isInitialLoading && filteredAnalyticsData.length > 0 && (
          <div className="space-y-4">
            <PortCallStatsCard
              aggregations={aggregations}
              mode="port"
              onSelectPortCall={handleSelectPortCall}
            />
            {(() => {
              // Calculate shared height for both charts based on distribution items
              const maxItems = 8;
              const itemCount = Math.min(
                aggregations.distribution.length,
                maxItems,
              );
              const chartHeight = Math.max(200, itemCount * 35);

              return (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <PortCallTimelineChart
                    portCalls={analyticsData}
                    dateRange={
                      dateRange?.from && dateRange?.to
                        ? { from: dateRange.from, to: dateRange.to }
                        : undefined
                    }
                    height={chartHeight}
                  />
                  <PortCallDistributionChart
                    distribution={aggregations.distribution}
                    mode="port"
                    maxItems={maxItems}
                    height={chartHeight}
                  />
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </ScrollArea>
  );
};

export default PortTimeline;
